import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { SvgDotsHorizontalTriple } from '@actual-app/components/icons/v1';
import { Menu } from '@actual-app/components/menu';
import { Popover } from '@actual-app/components/popover';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type {
  AccountEntity,
  CategoryEntity,
} from '@actual-app/core/types/models';

import { FinancialText } from '#components/FinancialText';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useSheetValue } from '#hooks/useSheetValue';
import type {
  PlanningBudgetCategorySummary,
  PlanningGoal,
  PlanningGoalStatus,
} from '#planning/types';
import { useLivePlanningBudgetCategorySummary } from '#planning/useLivePlanningBudgetCategorySummary';
import {
  getPlanningGoalLeftToSave,
  getPlanningGoalProgress,
  getPlanningGoalProjectedAmountAtTarget,
  getRemainingPayoffBalance,
  getPlanningGoalRecommendation,
  getPlanningGoalSavedAmount,
} from '#planning/utils';
import { selectedAccountsBalance } from '#spreadsheet/bindings';

import { getPlanningGoalIcon, getPlanningGoalLabel } from './goalMetadata';

type PlanningGoalCardProps = {
  goal: PlanningGoal;
  accountsById: Map<string, AccountEntity>;
  categoriesById: Map<string, CategoryEntity>;
  budgetCategoriesById: Map<string, PlanningBudgetCategorySummary>;
  currentMonth: string;
  onEdit: (goal: PlanningGoal) => void;
  onDelete: (goal: PlanningGoal) => void;
};

export function PlanningGoalCard({
  goal,
  accountsById,
  categoriesById,
  budgetCategoriesById,
  currentMonth,
  onEdit,
  onDelete,
}: PlanningGoalCardProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const isLoanPayoffGoal = goal.kind === 'loan-payoff';

  const linkedCategory = goal.categoryId
    ? categoriesById.get(goal.categoryId)
    : null;
  const fallbackBudgetCategory = goal.categoryId
    ? budgetCategoriesById.get(goal.categoryId)
    : undefined;
  const budgetCategory = useLivePlanningBudgetCategorySummary({
    categoryId: goal.categoryId,
    currentMonth,
    fallbackSummary: fallbackBudgetCategory,
  });
  const linkedPayoffAccountBalanceValue = useSheetValue<
    'account',
    `balance-set-${string}`
  >(
    selectedAccountsBalance(goal.payoffAccountId ? [goal.payoffAccountId] : []),
  );
  const linkedPayoffAccountBalance = goal.payoffAccountId
    ? linkedPayoffAccountBalanceValue
    : null;
  const remainingPayoffBalance = getRemainingPayoffBalance(
    linkedPayoffAccountBalance,
  );
  const savedAmount = getPlanningGoalSavedAmount(
    goal,
    budgetCategory ?? undefined,
    linkedPayoffAccountBalance,
  );
  const leftToSave = getPlanningGoalLeftToSave(
    goal,
    budgetCategory ?? undefined,
    linkedPayoffAccountBalance,
  );
  const progress = getPlanningGoalProgress(
    goal,
    budgetCategory ?? undefined,
    linkedPayoffAccountBalance,
  );
  const recommendation = getPlanningGoalRecommendation({
    goal,
    currentMonth,
    categorySummary: budgetCategory ?? undefined,
    payoffAccountBalance: linkedPayoffAccountBalance,
  });
  const projectedAmountAtTarget = getPlanningGoalProjectedAmountAtTarget({
    goal,
    currentMonth,
    categorySummary: budgetCategory ?? undefined,
    payoffAccountBalance: linkedPayoffAccountBalance,
    monthlyContribution: recommendation.currentCategoryBudgeted,
  });
  const Icon = getPlanningGoalIcon(goal.kind);

  const accountIds = useMemo(
    () =>
      [
        ...new Set(goal.allocations.map(allocation => allocation.accountId)),
      ].filter(accountId => accountId.length > 0),
    [goal.allocations],
  );
  const accountSummary = useMemo(() => {
    return accountIds
      .map(accountId => accountsById.get(accountId)?.name)
      .filter((accountName): accountName is string => !!accountName)
      .join(', ');
  }, [accountIds, accountsById]);
  const payoffAccountName = goal.payoffAccountId
    ? (accountsById.get(goal.payoffAccountId)?.name ?? null)
    : null;
  const linkedAccountsBalance = Math.max(
    0,
    useSheetValue<'account', `balance-set-${string}`>(
      selectedAccountsBalance(accountIds),
    ) ?? 0,
  );
  const accountCoverageDifference = Math.max(
    0,
    savedAmount - linkedAccountsBalance,
  );

  const statusPresentation = getPlanningStatusPresentation({
    goal,
    status: recommendation.status,
    t,
    format,
    monthlyContribution: recommendation.monthlyContribution,
    currentCategoryBudgeted: recommendation.currentCategoryBudgeted,
    projectedAmountAtTarget,
  });

  return (
    <Card
      style={{
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      <View style={{ padding: 20 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              flex: 1,
              minWidth: 0,
            }}
          >
            <View
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.noticeBackgroundLight,
                color: theme.reportsBlue,
                flexShrink: 0,
              }}
            >
              <Icon style={{ width: 22, height: 22, color: 'inherit' }} />
            </View>
            <View style={{ minWidth: 0, flex: 1 }}>
              <Text
                style={{
                  display: 'block',
                  fontSize: 26,
                  fontWeight: 600,
                  lineHeight: '1.15em',
                  overflowWrap: 'anywhere',
                }}
              >
                {goal.name || getPlanningGoalLabel(goal.kind, t)}
              </Text>
              <Text
                style={{
                  display: 'block',
                  marginTop: 4,
                  color: theme.pageTextSubdued,
                }}
              >
                {getPlanningGoalLabel(goal.kind, t)}
              </Text>
            </View>
          </View>

          <Button
            ref={triggerRef}
            variant="bare"
            aria-label={t('Goal options')}
            onPress={() => setMenuOpen(true)}
            style={{ padding: 6 }}
          >
            <SvgDotsHorizontalTriple style={{ width: 18, height: 18 }} />
          </Button>
          <Popover
            triggerRef={triggerRef}
            placement="bottom end"
            isOpen={isMenuOpen}
            onOpenChange={setMenuOpen}
          >
            <Menu
              items={[
                { name: 'edit', text: t('Edit goal') },
                { name: 'delete', text: t('Delete goal') },
              ]}
              onMenuSelect={item => {
                if (item === 'edit') {
                  onEdit(goal);
                } else if (item === 'delete') {
                  onDelete(goal);
                }
                setMenuOpen(false);
              }}
            />
          </Popover>
        </View>

        <View style={{ marginTop: 18 }}>
          <View
            style={{
              position: 'relative',
              height: 26,
              borderRadius: 6,
              overflow: 'hidden',
              backgroundColor: theme.noticeBackgroundLight,
            }}
          >
            <View
              style={{
                height: '100%',
                minWidth: progress > 0 ? 48 : 0,
                width: `${Math.max(progress * 100, 0)}%`,
                backgroundColor: theme.reportsBlue,
              }}
            />
            <Text
              style={{
                position: 'absolute',
                top: 5,
                left: 10,
                fontWeight: 600,
                color:
                  progress > 0.25 ? theme.buttonPrimaryText : theme.pageText,
              }}
            >
              {t('{{percent}}%', { percent: Math.round(progress * 100) })}
            </Text>
          </View>
        </View>

        <View style={{ marginTop: 14 }}>
          <Text
            style={{
              display: 'block',
              fontSize: 15,
              color: theme.pageTextSubdued,
            }}
          >
            {isLoanPayoffGoal ? t('Paid off so far') : t('Saved so far')}
          </Text>
          <PrivacyFilter>
            <FinancialText
              style={{
                display: 'block',
                marginTop: 4,
                fontSize: 34,
                lineHeight: '1.1em',
                fontWeight: 700,
              }}
            >
              {format(savedAmount, 'financial')}
            </FinancialText>
          </PrivacyFilter>
        </View>

        <View
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${theme.tableBorder}`,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 16,
          }}
        >
          <GoalMetric
            label={isLoanPayoffGoal ? t('Left to pay off') : t('Left to save')}
            value={format(leftToSave, 'financial')}
            tone="default"
            privacyFilter
          />
          <GoalMetric
            label={isLoanPayoffGoal ? t('Payoff target') : t('Goal')}
            value={format(goal.targetAmount, 'financial')}
            tone="default"
            privacyFilter
          />
          <GoalMetric
            label={
              isLoanPayoffGoal
                ? t('Payment needed each month')
                : t('Needed each month')
            }
            value={
              recommendation.monthlyContribution == null
                ? t('Flexible')
                : format(recommendation.monthlyContribution, 'financial')
            }
            tone="default"
            privacyFilter={recommendation.monthlyContribution != null}
          />
          <GoalMetric
            label={t('Budgeted this month')}
            value={
              budgetCategory
                ? format(budgetCategory.budgeted, 'financial')
                : t('Not linked')
            }
            tone={budgetCategory ? 'default' : 'subdued'}
            privacyFilter={budgetCategory != null}
          />
        </View>

        <View style={{ marginTop: 18 }}>
          <Text
            style={{
              display: 'inline-flex',
              borderRadius: 999,
              padding: '4px 10px',
              fontWeight: 600,
              backgroundColor: statusPresentation.backgroundColor,
              color: statusPresentation.textColor,
            }}
          >
            {statusPresentation.label}
          </Text>
          <Text
            style={{
              display: 'block',
              marginTop: 8,
              color: theme.pageTextSubdued,
            }}
          >
            {statusPresentation.description}
          </Text>
          {statusPresentation.projectionDescription ? (
            <Text
              style={{
                display: 'block',
                marginTop: 6,
                color: statusPresentation.textColor,
              }}
            >
              {statusPresentation.projectionDescription}
            </Text>
          ) : null}
          {accountIds.length > 0 ? (
            <Text
              style={{
                display: 'block',
                marginTop: 6,
                color:
                  !isLoanPayoffGoal && accountCoverageDifference > 0
                    ? theme.warningTextDark
                    : theme.pageTextSubdued,
              }}
            >
              {!isLoanPayoffGoal && accountCoverageDifference > 0
                ? t(
                    'Selected accounts are {{amount}} short of covering this goal.',
                    {
                      amount: format(accountCoverageDifference, 'financial'),
                    },
                  )
                : t(
                    isLoanPayoffGoal
                      ? 'Selected payment accounts currently hold {{amount}}.'
                      : 'Selected accounts currently hold {{amount}}.',
                    {
                      amount: format(linkedAccountsBalance, 'financial'),
                    },
                  )}
            </Text>
          ) : null}
          {isLoanPayoffGoal && goal.payoffAccountId ? (
            <Text
              style={{
                display: 'block',
                marginTop: 6,
                color: theme.pageTextSubdued,
              }}
            >
              {remainingPayoffBalance != null
                ? t('Current loan balance: {{amount}} owed.', {
                    amount: format(remainingPayoffBalance, 'financial'),
                  })
                : t('Current loan balance will appear here.')}
            </Text>
          ) : null}
        </View>

        <View
          style={{
            marginTop: 18,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
          }}
        >
          <GoalMetaPill
            label={t('Category')}
            value={
              budgetCategory
                ? `${budgetCategory.groupName} / ${budgetCategory.name}`
                : (linkedCategory?.name ?? t('Missing'))
            }
          />
          <GoalMetaPill
            label={t('Target')}
            value={
              goal.targetMonth
                ? monthUtils.format(goal.targetMonth, 'LLL yyyy')
                : t('No target date')
            }
          />
          <GoalMetaPill
            label={t('APR')}
            value={
              goal.annualPercentageRate > 0
                ? t('{{rate}}%', {
                    rate: formatRate(goal.annualPercentageRate),
                  })
                : t('None')
            }
          />
          {isLoanPayoffGoal ? (
            <GoalMetaPill
              label={t('Loan account')}
              value={payoffAccountName ?? t('Not linked')}
            />
          ) : null}
          <GoalMetaPill
            label={isLoanPayoffGoal ? t('Paying from') : t('Held in')}
            value={accountSummary || t('No accounts linked')}
          />
        </View>
      </View>
    </Card>
  );
}

function GoalMetric({
  label,
  value,
  tone,
  privacyFilter = false,
}: {
  label: string;
  value: string;
  tone: 'default' | 'subdued';
  privacyFilter?: boolean;
}) {
  const valueStyle = {
    display: 'block',
    marginTop: 4,
    fontSize: 20,
    lineHeight: '1.1em',
    fontWeight: 600,
    color: tone === 'default' ? theme.pageText : theme.pageTextSubdued,
    overflowWrap: 'anywhere' as const,
  };

  return (
    <View>
      <Text
        style={{
          display: 'block',
          color: theme.pageTextSubdued,
          fontSize: 13,
        }}
      >
        {label}
      </Text>
      {privacyFilter ? (
        <PrivacyFilter>
          <FinancialText style={valueStyle}>{value}</FinancialText>
        </PrivacyFilter>
      ) : (
        <Text style={valueStyle}>{value}</Text>
      )}
    </View>
  );
}

function GoalMetaPill({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{
        padding: '9px 11px',
        borderRadius: 6,
        backgroundColor: theme.tableBackground,
        border: `1px solid ${theme.tableBorder}`,
      }}
    >
      <Text
        style={{
          display: 'block',
          fontSize: 12,
          color: theme.pageTextSubdued,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          display: 'block',
          marginTop: 4,
          fontWeight: 500,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Text>
    </View>
  );
}

function formatRate(rate: number) {
  return rate.toFixed(2).replace(/\.?0+$/, '');
}

function getPlanningStatusPresentation({
  goal,
  status,
  t,
  format,
  monthlyContribution,
  currentCategoryBudgeted,
  projectedAmountAtTarget,
}: {
  goal: PlanningGoal;
  status: PlanningGoalStatus;
  t: ReturnType<typeof useTranslation>['t'];
  format: ReturnType<typeof useFormat>;
  monthlyContribution: number | null;
  currentCategoryBudgeted: number | null;
  projectedAmountAtTarget: number | null;
}) {
  const shortfall =
    monthlyContribution != null
      ? Math.max(0, monthlyContribution - (currentCategoryBudgeted ?? 0))
      : null;
  const targetMonthLabel = goal.targetMonth
    ? monthUtils.format(goal.targetMonth, 'LLL yyyy')
    : null;
  const projectedShortfall =
    projectedAmountAtTarget != null
      ? Math.max(0, goal.targetAmount - projectedAmountAtTarget)
      : null;
  const projectedLandingDescription =
    targetMonthLabel &&
    projectedAmountAtTarget != null &&
    projectedShortfall != null &&
    (status === 'below-target' || status === 'budget-this-month')
      ? goal.kind === 'loan-payoff'
        ? projectedShortfall > 0
          ? t(
              'At this pace you will likely have {{amount}} paid off by {{month}}, leaving {{shortfall}} to pay off.',
              {
                amount: format(projectedAmountAtTarget, 'financial'),
                month: targetMonthLabel,
                shortfall: format(projectedShortfall, 'financial'),
              },
            )
          : t(
              'At this pace you are still likely to pay this off by {{month}}.',
              {
                month: targetMonthLabel,
              },
            )
        : projectedShortfall > 0
          ? t(
              'At this pace you will likely have {{amount}} saved by {{month}}, leaving {{shortfall}} to save.',
              {
                amount: format(projectedAmountAtTarget, 'financial'),
                month: targetMonthLabel,
                shortfall: format(projectedShortfall, 'financial'),
              },
            )
          : t(
              'At this pace you are still likely to reach this goal by {{month}}.',
              {
                month: targetMonthLabel,
              },
            )
      : null;

  switch (status) {
    case 'funded':
      return {
        label: t('Funded'),
        description: t('This goal has already reached its target.'),
        projectionDescription: null,
        backgroundColor: theme.noticeBackgroundLight,
        textColor: theme.pageTextPositive,
      };
    case 'on-track':
      return {
        label: t('On track'),
        description: currentCategoryBudgeted
          ? t('{{amount}} is already budgeted this month.', {
              amount: format(currentCategoryBudgeted, 'financial'),
            })
          : t('This month is already covered.'),
        projectionDescription: null,
        backgroundColor: theme.noticeBackgroundLight,
        textColor: theme.noticeTextDark,
      };
    case 'below-target':
      return {
        label: t('Below target'),
        description:
          monthlyContribution != null &&
          currentCategoryBudgeted != null &&
          shortfall != null
            ? t(
                '{{budgeted}} of {{needed}} is budgeted this month. Short {{shortfall}}.',
                {
                  budgeted: format(currentCategoryBudgeted, 'financial'),
                  needed: format(monthlyContribution, 'financial'),
                  shortfall: format(shortfall, 'financial'),
                },
              )
            : t('This month is behind the target pace.'),
        projectionDescription: projectedLandingDescription,
        backgroundColor: theme.warningBackground,
        textColor: theme.warningTextDark,
      };
    case 'budget-this-month':
      return {
        label: t('Budget this month'),
        description:
          shortfall != null
            ? t('Nothing is budgeted this month. Short {{amount}}.', {
                amount: format(shortfall, 'financial'),
              })
            : t('Add this goal to the budget this month.'),
        projectionDescription: projectedLandingDescription,
        backgroundColor: theme.warningBackground,
        textColor: theme.warningTextDark,
      };
    case 'missing-category':
      return {
        label: t('Category needed'),
        description: t('Link a budget category to track monthly progress.'),
        projectionDescription: null,
        backgroundColor: theme.errorBackground,
        textColor: theme.errorTextDark,
      };
    case 'flexible':
      return {
        label: t('Flexible'),
        description: t('Set a target date whenever you want a monthly pace.'),
        projectionDescription: null,
        backgroundColor: theme.tableBackground,
        textColor: theme.pageTextSubdued,
      };
  }
}
